// GENERATED by scripts/gen-scan-fields.mjs — do not edit by hand.
//
// Every key the recognition API returns, read off the contract's own schema
// (packages/contracts/src/scan.ts). The landing page and the field explorer
// publish this as a table, so a key that moves in the contract moves here in
// the same change or `pnpm check` fails.
//
// The headings and blurbs the groups are shown under are editorial and live in
// ./fields.ts; everything in this file comes from the schema.

export interface ScanShapeNode {
  readonly name: string;
  /** The type as the table prints it, e.g. `string`, `enum`, `object[]`. */
  readonly type: string;
  /** True when the value can be null — a known-but-absent value, never a missing key. */
  readonly nullable: boolean;
  readonly description: string;
  readonly children?: readonly ScanShapeNode[];
}

export const scanShape: readonly ScanShapeNode[] = [
  {
    name: "meta",
    type: "object",
    nullable: false,
    description: "",
    children: [
      {
        name: "schema_version",
        type: "string",
        nullable: false,
        description:
          "The version of this body, always `1.0`. A consumer that pins this value checks that the body is the one it was written against.",
      },
      {
        name: "id",
        type: "string",
        nullable: false,
        description:
          "Scan identifier: a UUID version 7 (RFC 9562), canonical lower-case `8-4-4-4-12`. Its leading 48 bits are the millisecond the scan was made, so ids sort in the order the scans happened – but treat the value as opaque: nothing else about it is part of the contract.",
      },
      {
        name: "status",
        type: "enum",
        nullable: false,
        description:
          "Outcome of a scan. Exactly these five string values; there are no numeric codes.",
      },
      {
        name: "billed",
        type: "boolean",
        nullable: false,
        description:
          "Whether the scan met the billing rule. On a live key, `true` means one credit was charged. On a sandbox key nothing is ever charged, and the flag only shows whether the same scan would have been.",
      },
      {
        name: "confidence",
        type: "enum",
        nullable: false,
        description:
          "How strongly the recognition backs this reading as a whole. Each entry of `fields` carries its own band as well, and they can differ from this one.",
      },
      {
        name: "timing",
        type: "object",
        nullable: true,
        description:
          "The split of the request's time. Null on a scan made before the split existed and read back out of storage: only the engine call was timed then, and the two halves cannot be recovered from it.",
        children: [
          {
            name: "upload_ms",
            type: "integer",
            nullable: false,
            description:
              "From the request's headers reaching the server to its body being received and validated, with your key resolved and its rate limit checked. The allowance, idempotency and credit gates are claimed after this number is taken, so they are not in it. Dominated by your own connection and by how large the image is – this is the half you can shrink, by sending a smaller picture from closer by.",
          },
          {
            name: "processing_ms",
            type: "integer",
            nullable: false,
            description: "The recognition itself – the server-side engine call.",
          },
          {
            name: "total_ms",
            type: "integer",
            nullable: false,
            description:
              "From the request arriving to the result being complete. At least `upload_ms + processing_ms`; the remainder is the gates that run after `upload_ms` is taken – the allowance, the idempotency check and the credit hold – plus preparing the result images and mapping the engine's output into this body. It stops there: writing the history row and serializing the response happen after the number is fixed, so the same figure is stored and returned.",
          },
        ],
      },
      {
        name: "created_at",
        type: "date-time",
        nullable: false,
        description: "Timestamp, ISO-8601 in UTC (`YYYY-MM-DDTHH:MM:SSZ`).",
      },
      {
        name: "reference",
        type: "string",
        nullable: true,
        description: "The request's `reference`, echoed back.",
      },
    ],
  },
  {
    name: "document",
    type: "object",
    nullable: true,
    description: "",
    children: [
      {
        name: "kind",
        type: "string",
        nullable: false,
        description: "Document type, e.g. `passport`.",
      },
      {
        name: "country",
        type: "string",
        nullable: true,
        description:
          "The state that issued the document, as its three-letter code. The same reading as `issuing_state` under the name most callers filter on; null when nothing was read.",
      },
      {
        name: "country_name",
        type: "string",
        nullable: true,
        description: "The issuing state's name, as read from the document.",
      },
      {
        name: "issuing_state",
        type: "string",
        nullable: true,
        description:
          "The issuing state, the three-letter code the document writes – the same reading as `country`, under the name the machine-readable zone gives it.",
      },
      {
        name: "issuing_state_detail",
        type: "object",
        nullable: true,
        description:
          "The issuing state's code resolved: as written, as ISO 3166-1 alpha-3 and alpha-2, and its category. Null when no code was read.",
        children: [
          {
            name: "code",
            type: "string",
            nullable: false,
            description:
              "The code as the document writes it, without the zone's `<` filler: `D` for Germany, `GBD` for a British Overseas Territories citizen.",
          },
          {
            name: "alpha3",
            type: "string",
            nullable: true,
            description:
              "The ISO 3166-1 alpha-3 code of the state the code stands for, or null when ISO has none (Kosovo, organisations, stateless persons, refugees, specimens, unknown codes).",
          },
          {
            name: "alpha2",
            type: "string",
            nullable: true,
            description: "The ISO 3166-1 alpha-2 code of the same state, or null with `alpha3`.",
          },
          {
            name: "category",
            type: "enum",
            nullable: false,
            description:
              "What a document's country code is. `iso`: an ISO 3166-1 alpha-3 code. `icao_variant`: an ICAO spelling of an ISO state (`D` for Germany). `british_nationality`: a British nationality class (`GBD`, `GBN`, `GBO`, `GBP`, `GBS`). `kosovo`: `RKS`, which ISO does not list. `united_nations`: `UNO`, `UNA`, `UNK`. `organization`: an issuing authority that is not a state (`EUE`, `XPO`, `IAO` and the other `X..` codes of ICAO Doc 9303). `stateless`: `XXA`. `refugee`: `XXB`, `XXC`. `unspecified`: `XXX`. `deprecated`: a code ISO has withdrawn (`ANT`, `NTZ`). `specimen`: `UTO`, the fictional state of specimen documents. `unknown`: a code in none of these lists.",
          },
        ],
      },
      {
        name: "type_name",
        type: "string",
        nullable: true,
        description:
          "The document type under its full name. Null on a scan read back from storage, which keeps no engine output.",
      },
      {
        name: "type_confidence",
        type: "enum",
        nullable: false,
        description: "How strongly the document-type match is backed.",
      },
      {
        name: "number",
        type: "string",
        nullable: true,
        description:
          "The document number, as printed. Null when none was read. A series the document prints separately is in `series`, never folded into this value.",
      },
      {
        name: "series",
        type: "string",
        nullable: true,
        description:
          "The document series, for a document that prints one as a field of its own. Null when the document carries none or none was read; it is never split out of `number`.",
      },
      {
        name: "issue_date",
        type: "date",
        nullable: true,
        description:
          "The date the document was issued, ISO-8601 (`YYYY-MM-DD`). Null when none was read.",
      },
      {
        name: "expiry_date",
        type: "date",
        nullable: true,
        description:
          "The date the document expires, ISO-8601 (`YYYY-MM-DD`). Null when none was read; `is_expired` and `days_remaining` are computed from it.",
      },
      {
        name: "is_expired",
        type: "boolean",
        nullable: true,
        description:
          "Whether the document had already expired when the scan was made. Null when no expiry date was read, which is a different answer from `false`.",
      },
      {
        name: "days_remaining",
        type: "integer",
        nullable: true,
        description: "Days until expiry at the time of the scan; negative once expired.",
      },
    ],
  },
  {
    name: "holder",
    type: "object",
    nullable: true,
    description: "",
    children: [
      {
        name: "given_names",
        type: "string",
        nullable: true,
        description: "The holder's given names, as printed. Null when none was read.",
      },
      {
        name: "surname",
        type: "string",
        nullable: true,
        description: "The holder's surname, as printed. Null when none was read.",
      },
      {
        name: "full_name",
        type: "string",
        nullable: true,
        description:
          "The holder's name as the document prints it in one combined field. Null when the document carries no such field – it is not composed from the two above.",
      },
      {
        name: "birth_date",
        type: "date",
        nullable: true,
        description:
          "The holder's date of birth, ISO-8601 (`YYYY-MM-DD`). Null when none was read.",
      },
      {
        name: "sex",
        type: "enum",
        nullable: true,
        description:
          "The sex the document states: `M`, `F`, or `X` for unspecified. Null when none was read.",
      },
      {
        name: "nationality",
        type: "string",
        nullable: true,
        description:
          "The holder's nationality, the three-letter code the document writes. Null when none was read; it is not assumed from the issuing state.",
      },
      {
        name: "nationality_detail",
        type: "object",
        nullable: true,
        description:
          "The nationality code resolved: as written, as ISO 3166-1 alpha-3 and alpha-2, and its category – `stateless` or `refugee` for a holder without a nationality. Null when no code was read.",
        children: [
          {
            name: "code",
            type: "string",
            nullable: false,
            description:
              "The code as the document writes it, without the zone's `<` filler: `D` for Germany, `GBD` for a British Overseas Territories citizen.",
          },
          {
            name: "alpha3",
            type: "string",
            nullable: true,
            description:
              "The ISO 3166-1 alpha-3 code of the state the code stands for, or null when ISO has none (Kosovo, organisations, stateless persons, refugees, specimens, unknown codes).",
          },
          {
            name: "alpha2",
            type: "string",
            nullable: true,
            description: "The ISO 3166-1 alpha-2 code of the same state, or null with `alpha3`.",
          },
          {
            name: "category",
            type: "enum",
            nullable: false,
            description:
              "What a document's country code is. `iso`: an ISO 3166-1 alpha-3 code. `icao_variant`: an ICAO spelling of an ISO state (`D` for Germany). `british_nationality`: a British nationality class (`GBD`, `GBN`, `GBO`, `GBP`, `GBS`). `kosovo`: `RKS`, which ISO does not list. `united_nations`: `UNO`, `UNA`, `UNK`. `organization`: an issuing authority that is not a state (`EUE`, `XPO`, `IAO` and the other `X..` codes of ICAO Doc 9303). `stateless`: `XXA`. `refugee`: `XXB`, `XXC`. `unspecified`: `XXX`. `deprecated`: a code ISO has withdrawn (`ANT`, `NTZ`). `specimen`: `UTO`, the fictional state of specimen documents. `unknown`: a code in none of these lists.",
          },
        ],
      },
    ],
  },
  {
    name: "fields",
    type: "object[]",
    nullable: false,
    description: "",
    children: [
      {
        name: "id",
        type: "string",
        nullable: false,
        description:
          "Identity of this entry, unique across `fields`: the key and the language identifier the value was read as, plus an occurrence counter when the same pair is reported twice. `name` is the semantic key and repeats – a document that carries a field in two scripts yields one entry per language – so use `id`, not `name`, to address or key a single entry.",
      },
      {
        name: "name",
        type: "string",
        nullable: false,
        description: "Our stable snake_case key.",
      },
      {
        name: "label",
        type: "string",
        nullable: false,
        description: "Our human label.",
      },
      {
        name: "category",
        type: "enum",
        nullable: false,
        description: "Which group of the report a field belongs to.",
      },
      {
        name: "value",
        type: "string",
        nullable: true,
        description:
          "The value of this reading: the national-script spelling on a national-script reading, the transliterated Latin value on the default reading (the one whose `id` carries the identifier `0`). Null when the field is empty.",
      },
      {
        name: "language",
        type: "string",
        nullable: true,
        description:
          "The language this reading was made in, e.g. `Greek`. The default Latin-script reading – the document's own Latin page and the machine-readable zone, `id` suffix `@0` – is `English`. Null only on `days_to_expire`, a number computed from the expiry date rather than text read in any language.",
      },
      {
        name: "confidence",
        type: "enum",
        nullable: false,
        description:
          "How strongly the recognition backs this value: `high`, `medium` or `low`. An unknown or missing probability reads as `low`.",
      },
    ],
  },
  {
    name: "mrz",
    type: "object",
    nullable: false,
    description: "",
    children: [
      {
        name: "status",
        type: "enum",
        nullable: false,
        description:
          "Verdict on the machine-readable zone: `passed` (present, every check digit valid and nothing contradicting the printed page), `failed` (present but something did not check out) or `absent` (the document carries none).",
      },
      {
        name: "reason",
        type: "string",
        nullable: true,
        description:
          "One plain sentence naming what did not check out, for `failed`; null otherwise.",
      },
      {
        name: "lines",
        type: "string[]",
        nullable: true,
        description:
          "The zone's lines in order, exactly as read – two for a TD3 passport, three for a TD1 card. The zone's alphabet is `A-Z`, `0-9` and the filler `<`, so a line carries no whitespace. Null when the document carries none.",
      },
      {
        name: "text",
        type: "string",
        nullable: true,
        description:
          "The same lines run together with nothing between them: one unbroken string, with no newlines and no spaces. Null when there are none.",
      },
    ],
  },
  {
    name: "images",
    type: "object",
    nullable: false,
    description: "",
    children: [
      {
        name: "document_crop",
        type: "string",
        nullable: true,
        description:
          "The document itself, cropped out of the uploaded picture and deskewed – the front side of a card, the data page of a booklet.",
      },
      {
        name: "rear",
        type: "string",
        nullable: true,
        description:
          "The reverse side of the document, when the picture carried one and a crop of it was produced.",
      },
      {
        name: "main_photo",
        type: "string",
        nullable: true,
        description: "The holder's photograph as printed on the document.",
      },
      {
        name: "signature",
        type: "string",
        nullable: true,
        description: "The holder's signature as printed on the document.",
      },
      {
        name: "watermark_face",
        type: "string",
        nullable: true,
        description:
          "The faint second copy of the holder's face printed into the page as a security feature – a different image from `main_photo`, and the one a verifier compares against it. Null when the document carries none.",
      },
      {
        name: "barcode",
        type: "string",
        nullable: true,
        description: "The barcode area of the document, when it carries one.",
      },
      {
        name: "chip",
        type: "string",
        nullable: true,
        description: "The chip area of the document, when it carries one.",
      },
    ],
  },
  {
    name: "quality",
    type: "object",
    nullable: false,
    description: "",
    children: [
      {
        name: "overall",
        type: "enum",
        nullable: false,
        description:
          "Whether the uploaded picture was good enough to recognize from. `not_checked` when nothing measured it – a scan read back from storage, which keeps no engine output.",
      },
    ],
  },
  {
    name: "authenticity",
    type: "object",
    nullable: false,
    description: "",
    children: [
      {
        name: "overall",
        type: "enum",
        nullable: false,
        description:
          "`not_checked` under the recognition-only scenario; populated by authenticity verification later.",
      },
      {
        name: "checks",
        type: "object[]",
        nullable: false,
        description:
          "One entry per authenticity check that ran. Always present; empty under the recognition-only scenario, where nothing ran.",
        children: [
          {
            name: "name",
            type: "string",
            nullable: false,
            description: "Our stable snake_case key for this check.",
          },
          {
            name: "label",
            type: "string",
            nullable: false,
            description: "Our human label for this check.",
          },
          {
            name: "result",
            type: "enum",
            nullable: false,
            description: "Outcome of a single check.",
          },
          {
            name: "detail",
            type: "string",
            nullable: true,
            description:
              "One plain sentence about what this check saw, or null when it has nothing to add.",
          },
        ],
      },
    ],
  },
];
